import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Eye, GitBranch, Save, Send } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { apiClient } from '../lib/api'
import { ProposalAssetsTab } from './ProposalAssetsTab'
import { ProposalHistoryDialog } from './ProposalHistoryDialog'
import { Badge } from './ui/Badge'
import { Button } from './ui/Button'
import { useConfirm } from './ui/ConfirmDialog'
import { Drawer } from './ui/Drawer'
import { Input, Label, Select, Textarea } from './ui/Input'

type Proposal = {
  id: string
  code: string
  revision: number
  type: 'PRV' | 'CTR'
  status: string
  title: string
  subtitle: string | null
  clientId: string | null
  clientName: string | null
  totalAmountEur: string | null
  vatRate: string
  validityDays: number
  validUntil: string | null
  content: Record<string, unknown>
}

type Tab =
  | 'meta'
  | 'client'
  | 'summary'
  | 'sections'
  | 'pricing'
  | 'payment'
  | 'conditions'
  | 'acceptance'
  | 'asset'

const tabs: Array<{ id: Tab; label: string }> = [
  { id: 'meta', label: 'Metadati' },
  { id: 'client', label: 'Cliente' },
  { id: 'summary', label: 'Sommario · Contesto' },
  { id: 'sections', label: 'Sezioni libere' },
  { id: 'pricing', label: 'Prezzi · Configurazioni' },
  { id: 'payment', label: 'Pagamento · Manutenzione' },
  { id: 'conditions', label: 'Condizioni' },
  { id: 'acceptance', label: 'Accettazione' },
  { id: 'asset', label: 'Asset' },
]

// biome-ignore lint/suspicious/noExplicitAny: editing freeform structured content
type ProposalContent = any

export function ProposalEditorDrawer({
  proposalId,
  onClose,
}: {
  proposalId: string
  onClose: () => void
}) {
  const qc = useQueryClient()
  const confirm = useConfirm()
  const [tab, setTab] = useState<Tab>('meta')
  const [content, setContent] = useState<ProposalContent | null>(null)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [meta, setMeta] = useState({
    title: '',
    subtitle: '',
    totalAmountEur: '',
    validityDays: '30',
  })

  const proposalQ = useQuery({
    queryKey: ['proposal', proposalId],
    queryFn: () =>
      apiClient.get<{ found: boolean; proposal: Proposal }>(`/proposals/${proposalId}`),
  })

  useEffect(() => {
    if (proposalQ.data?.found) {
      const p = proposalQ.data.proposal
      setContent(p.content)
      setMeta({
        title: p.title,
        subtitle: p.subtitle ?? '',
        totalAmountEur: p.totalAmountEur ?? '',
        validityDays: String(p.validityDays),
      })
    }
  }, [proposalQ.data])

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiClient.patch(`/proposals/${proposalId}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['proposals'] })
      qc.invalidateQueries({ queryKey: ['proposal', proposalId] })
    },
  })

  const send = useMutation({
    mutationFn: () => apiClient.post(`/proposals/${proposalId}/status`, { status: 'sent' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['proposals'] })
      qc.invalidateQueries({ queryKey: ['proposal', proposalId] })
    },
  })

  function update(path: string[], value: unknown) {
    setContent((prev: ProposalContent) => setDeep(prev, path, value))
  }

  function onSave() {
    save.mutate({
      title: meta.title,
      subtitle: meta.subtitle || undefined,
      totalAmountEur: meta.totalAmountEur ? Number(meta.totalAmountEur) : undefined,
      validityDays: Number(meta.validityDays) || 30,
      content,
    })
  }

  if (proposalQ.isLoading || !content) {
    return (
      <Drawer open onClose={onClose} title="Caricamento…" width="lg">
        <div className="p-6 text-zinc-500 text-sm">Caricamento proposta…</div>
      </Drawer>
    )
  }

  if (!proposalQ.data?.found) {
    return (
      <Drawer open onClose={onClose} title="Non trovata" width="md">
        <div className="p-6 text-zinc-500 text-sm">Proposta non trovata.</div>
      </Drawer>
    )
  }

  const p = proposalQ.data.proposal
  const isDraft = p.status === 'draft'

  return (
    <Drawer
      open
      onClose={onClose}
      title={`${p.code} R${p.revision}`}
      subtitle={p.title}
      badges={
        <>
          <Badge variant={p.type === 'CTR' ? 'brand' : 'info'}>{p.type}</Badge>
          <Badge variant="muted">{p.status}</Badge>
          {!isDraft && <Badge variant="warning">solo lettura</Badge>}
          {p.validUntil && (
            <Badge variant="default">
              scade {new Date(p.validUntil).toLocaleDateString('it-IT')}
            </Badge>
          )}
        </>
      }
      width="lg"
    >
      {/* Read-only banner for non-draft proposals */}
      {!isDraft && (
        <div className="mx-4 mt-3 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
          Questa proposta è in stato <strong>{p.status}</strong> e non può essere modificata. Per
          cambiarne il contenuto crea una nuova revisione (R{p.revision + 1}) dal menu della lista.
          La preview e lo storico restano consultabili.
        </div>
      )}

      {/* Toolbar */}
      <div className="px-4 py-2 border-b border-zinc-800/80 bg-zinc-950/50 flex items-center gap-2">
        <Button
          variant="primary"
          size="sm"
          onClick={onSave}
          disabled={save.isPending || !isDraft}
          title={!isDraft ? 'Solo le bozze sono modificabili — crea una nuova revisione' : ''}
        >
          <Save className="size-3.5" />
          {save.isPending ? 'Salvataggio…' : 'Salva'}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => window.open(`/api/proposals/${proposalId}/preview`, '_blank')}
        >
          <Eye className="size-3.5" /> Anteprima
        </Button>
        <Button variant="ghost" size="sm" onClick={() => setHistoryOpen(true)}>
          <GitBranch className="size-3.5" /> Storico
        </Button>
        {p.status === 'draft' && (
          <Button
            variant="outline"
            size="sm"
            onClick={async () => {
              const ok = await confirm({
                title: 'Marcare come inviata?',
                message:
                  'La proposta risulterà inviata al cliente, con la data di oggi. Procedere solo dopo aver effettivamente trasmesso il PDF (PEC, email o consegna).',
                confirmLabel: 'Marca come inviata',
                tone: 'info',
              })
              if (ok) send.mutate()
            }}
            disabled={send.isPending}
          >
            <Send className="size-3.5" /> Marca come inviata
          </Button>
        )}
        {save.isError && <span className="text-xs text-pink-300 ml-auto">Errore salvataggio</span>}
        {save.isSuccess && !save.isPending && (
          <span className="text-xs text-emerald-300 ml-auto">✓ Salvato</span>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-zinc-800/80 flex overflow-x-auto">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`px-4 py-2.5 text-xs font-medium whitespace-nowrap transition border-b-2 -mb-px ${
              tab === t.id
                ? 'text-cyan-300 border-cyan-400'
                : 'text-zinc-500 border-transparent hover:text-zinc-200'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/*
       * Tab content — wrapped in a <fieldset disabled> when the proposal is
       * not in draft. HTML's fieldset[disabled] natively disables every
       * descendant form control (input/select/textarea/button at any depth),
       * including those rendered by sub-components. `className="contents"`
       * makes the fieldset invisible to the layout (display:contents) so
       * spacing/grid behave exactly as before.
       *
       * For the asset tab we also pass readOnly so it hides its uploader
       * (drag-drop divs aren't form controls and would otherwise still
       * accept drops).
       */}
      <fieldset
        disabled={!isDraft}
        className={`contents ${!isDraft ? '[&_input]:cursor-not-allowed [&_select]:cursor-not-allowed [&_textarea]:cursor-not-allowed' : ''}`}
      >
        <div className="p-5 space-y-4">
          {tab === 'meta' && (
            <MetaTab meta={meta} setMeta={setMeta} content={content} update={update} />
          )}
          {tab === 'client' && <ClientTab content={content} update={update} />}
          {tab === 'summary' && <SummaryContextTab content={content} update={update} />}
          {tab === 'sections' && (
            <SectionsTab content={content} update={update} setContent={setContent} />
          )}
          {tab === 'pricing' && (
            <PricingTab content={content} update={update} setContent={setContent} />
          )}
          {tab === 'payment' && (
            <PaymentTab content={content} update={update} setContent={setContent} />
          )}
          {tab === 'conditions' && <ConditionsTab content={content} update={update} />}
          {tab === 'acceptance' && <AcceptanceTab content={content} update={update} />}
          {tab === 'asset' && <ProposalAssetsTab proposalId={proposalId} readOnly={!isDraft} />}
        </div>
      </fieldset>

      {historyOpen && (
        <ProposalHistoryDialog
          proposalId={proposalId}
          currentRevision={p.revision}
          onClose={() => setHistoryOpen(false)}
        />
      )}
    </Drawer>
  )
}

// ============================================================
// Tab: Metadata
// ============================================================
function MetaTab({
  meta,
  setMeta,
  content,
  update,
}: {
  meta: { title: string; subtitle: string; totalAmountEur: string; validityDays: string }
  setMeta: (m: typeof meta) => void
  content: ProposalContent
  update: (path: string[], v: unknown) => void
}) {
  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="m-title">Titolo</Label>
        <Input
          id="m-title"
          value={meta.title}
          onChange={(e) => setMeta({ ...meta, title: e.target.value })}
        />
      </div>
      <div>
        <Label htmlFor="m-sub">Sottotitolo</Label>
        <Input
          id="m-sub"
          value={meta.subtitle}
          onChange={(e) => setMeta({ ...meta, subtitle: e.target.value })}
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label htmlFor="m-total">Importo totale (EUR, IVA esclusa)</Label>
          <Input
            id="m-total"
            type="number"
            step="0.01"
            value={meta.totalAmountEur}
            onChange={(e) => setMeta({ ...meta, totalAmountEur: e.target.value })}
          />
        </div>
        <div>
          <Label htmlFor="m-valid">Validità (giorni)</Label>
          <Input
            id="m-valid"
            type="number"
            value={meta.validityDays}
            onChange={(e) => setMeta({ ...meta, validityDays: e.target.value })}
          />
        </div>
      </div>
      <div>
        <Label htmlFor="m-issuedOn">Emessa il</Label>
        <Input
          id="m-issuedOn"
          type="date"
          value={content.header?.issuedOn ?? ''}
          onChange={(e) => update(['header', 'issuedOn'], e.target.value)}
        />
      </div>
      <div>
        <Label htmlFor="m-doc-title">Titolo documento (header)</Label>
        <Input
          id="m-doc-title"
          value={content.header?.title ?? ''}
          onChange={(e) => update(['header', 'title'], e.target.value)}
        />
      </div>
      <div>
        <Label htmlFor="m-doc-subtitle">Sottotitolo documento (header)</Label>
        <Input
          id="m-doc-subtitle"
          value={content.header?.subtitle ?? ''}
          onChange={(e) => update(['header', 'subtitle'], e.target.value)}
        />
      </div>
    </div>
  )
}

// ============================================================
// Tab: Client snapshot
// ============================================================
function ClientTab({
  content,
  update,
}: {
  content: ProposalContent
  update: (path: string[], v: unknown) => void
}) {
  const c = content.client ?? {}
  return (
    <div className="space-y-4">
      <p className="text-xs text-zinc-500 italic">
        Snapshot anagrafica al momento dell&apos;invio. Resta congelato anche se il cliente viene
        aggiornato in seguito.
      </p>
      <div>
        <Label htmlFor="c-name">Ragione sociale</Label>
        <Input
          id="c-name"
          value={c.ragioneSociale ?? ''}
          onChange={(e) => update(['client', 'ragioneSociale'], e.target.value)}
        />
      </div>
      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-6">
          <Label htmlFor="c-addr">Indirizzo</Label>
          <Input
            id="c-addr"
            value={c.indirizzo ?? ''}
            onChange={(e) => update(['client', 'indirizzo'], e.target.value)}
          />
        </div>
        <div className="col-span-2">
          <Label htmlFor="c-cap">CAP</Label>
          <Input
            id="c-cap"
            value={c.cap ?? ''}
            onChange={(e) => update(['client', 'cap'], e.target.value)}
          />
        </div>
        <div className="col-span-3">
          <Label htmlFor="c-citta">Città</Label>
          <Input
            id="c-citta"
            value={c.citta ?? ''}
            onChange={(e) => update(['client', 'citta'], e.target.value)}
          />
        </div>
        <div className="col-span-1">
          <Label htmlFor="c-prov">Prov</Label>
          <Input
            id="c-prov"
            value={c.provincia ?? ''}
            onChange={(e) => update(['client', 'provincia'], e.target.value)}
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label htmlFor="c-vat">P.IVA</Label>
          <Input
            id="c-vat"
            value={c.partitaIva ?? ''}
            onChange={(e) => update(['client', 'partitaIva'], e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="c-cf">Codice fiscale</Label>
          <Input
            id="c-cf"
            value={c.codiceFiscale ?? ''}
            onChange={(e) => update(['client', 'codiceFiscale'], e.target.value)}
          />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <Label htmlFor="c-email">Email</Label>
          <Input
            id="c-email"
            value={c.email ?? ''}
            onChange={(e) => update(['client', 'email'], e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="c-tel">Telefono</Label>
          <Input
            id="c-tel"
            value={c.telefono ?? ''}
            onChange={(e) => update(['client', 'telefono'], e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="c-web">Sito web</Label>
          <Input
            id="c-web"
            value={c.sitoWeb ?? ''}
            onChange={(e) => update(['client', 'sitoWeb'], e.target.value)}
          />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <Label htmlFor="c-ref-name">Referente</Label>
          <Input
            id="c-ref-name"
            value={c.referente?.nome ?? ''}
            onChange={(e) => update(['client', 'referente', 'nome'], e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="c-ref-role">Ruolo</Label>
          <Input
            id="c-ref-role"
            value={c.referente?.ruolo ?? ''}
            onChange={(e) => update(['client', 'referente', 'ruolo'], e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="c-ref-email">Email referente</Label>
          <Input
            id="c-ref-email"
            value={c.referente?.email ?? ''}
            onChange={(e) => update(['client', 'referente', 'email'], e.target.value)}
          />
        </div>
      </div>
      <ProfiloEditor
        profilo={c.profilo ?? []}
        onChange={(next) => update(['client', 'profilo'], next)}
      />
    </div>
  )
}

function ProfiloEditor({
  profilo,
  onChange,
}: {
  profilo: Array<{ label: string; value: string }>
  onChange: (next: Array<{ label: string; value: string }>) => void
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <Label>Profilo tecnico (label/value)</Label>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onChange([...profilo, { label: '', value: '' }])}
        >
          + Aggiungi riga
        </Button>
      </div>
      <div className="space-y-2">
        {profilo.map((r, i) => (
          <div
            // biome-ignore lint/suspicious/noArrayIndexKey: editable list with no stable id
            key={i}
            className="grid grid-cols-12 gap-2"
          >
            <Input
              className="col-span-4"
              value={r.label}
              placeholder="Etichetta"
              onChange={(e) => {
                const next = [...profilo]
                const row = next[i] ?? { label: '', value: '' }
                next[i] = { ...row, label: e.target.value }
                onChange(next)
              }}
            />
            <Input
              className="col-span-7"
              value={r.value}
              placeholder="Valore"
              onChange={(e) => {
                const next = [...profilo]
                const row = next[i] ?? { label: '', value: '' }
                next[i] = { ...row, value: e.target.value }
                onChange(next)
              }}
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onChange(profilo.filter((_, j) => j !== i))}
              className="col-span-1"
            >
              ✕
            </Button>
          </div>
        ))}
      </div>
    </div>
  )
}

// ============================================================
// Tab: Summary + Context
// ============================================================
function SummaryContextTab({
  content,
  update,
}: {
  content: ProposalContent
  update: (path: string[], v: unknown) => void
}) {
  const c = content.context ?? {}
  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="exec">Sommario esecutivo</Label>
        <Textarea
          id="exec"
          value={content.executiveSummary ?? ''}
          onChange={(e) => update(['executiveSummary'], e.target.value)}
          rows={4}
          placeholder="Sintesi 3-5 righe di cosa si sta proponendo."
        />
        <p className="text-[11px] text-zinc-500 mt-1">
          Markdown lite: **grassetto**, *corsivo*, `codice`, [link](url), invii a capo singoli.
        </p>
      </div>
      <div>
        <Label htmlFor="obj">Obiettivo (box blu)</Label>
        <Textarea
          id="obj"
          value={c.obiettivoBox ?? ''}
          onChange={(e) => update(['context', 'obiettivoBox'], e.target.value)}
          rows={3}
        />
      </div>
      <div>
        <Label htmlFor="biz">Descrizione business</Label>
        <Textarea
          id="biz"
          value={c.descrizioneBusiness ?? ''}
          onChange={(e) => update(['context', 'descrizioneBusiness'], e.target.value)}
          rows={4}
        />
      </div>
      <StringList
        label="Attività previste"
        items={c.attivitaPreviste ?? []}
        onChange={(next) => update(['context', 'attivitaPreviste'], next)}
      />
      <div>
        <Label htmlFor="rif">Riferimento evento (opzionale)</Label>
        <Input
          id="rif"
          value={c.riferimentoEvento ?? ''}
          onChange={(e) => update(['context', 'riferimentoEvento'], e.target.value)}
          placeholder="Es. videocall del 28/04/2026"
        />
      </div>
    </div>
  )
}

function StringList({
  label,
  items,
  onChange,
  placeholder,
}: {
  label: string
  items: string[]
  onChange: (next: string[]) => void
  placeholder?: string
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <Label>{label}</Label>
        <Button variant="ghost" size="sm" onClick={() => onChange([...items, ''])}>
          + Aggiungi
        </Button>
      </div>
      <div className="space-y-2">
        {items.map((s, i) => (
          <div
            // biome-ignore lint/suspicious/noArrayIndexKey: editable string list
            key={i}
            className="flex gap-2"
          >
            <Input
              className="flex-1"
              value={s}
              placeholder={placeholder}
              onChange={(e) => {
                const next = [...items]
                next[i] = e.target.value
                onChange(next)
              }}
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onChange(items.filter((_, j) => j !== i))}
            >
              ✕
            </Button>
          </div>
        ))}
      </div>
    </div>
  )
}

// ============================================================
// Tab: Sections (advanced — JSON editor)
// ============================================================
function SectionsTab({
  content,
  setContent,
}: {
  content: ProposalContent
  update: (path: string[], v: unknown) => void
  setContent: (next: ProposalContent) => void
}) {
  const [jsonText, setJsonText] = useState(() => JSON.stringify(content.sections ?? [], null, 2))
  const [parseError, setParseError] = useState<string | null>(null)

  const sectionCounts = useMemo(() => {
    const sections = (content.sections ?? []) as Array<{ type: string }>
    const counts: Record<string, number> = {}
    for (const s of sections) counts[s.type] = (counts[s.type] ?? 0) + 1
    return counts
  }, [content.sections])

  function applyJson() {
    try {
      const parsed = JSON.parse(jsonText)
      if (!Array.isArray(parsed)) {
        setParseError('Deve essere un array di sezioni.')
        return
      }
      setContent({ ...content, sections: parsed })
      setParseError(null)
    } catch (err) {
      setParseError(err instanceof Error ? err.message : 'JSON non valido')
    }
  }

  return (
    <div className="space-y-3">
      <div className="text-xs text-zinc-500 leading-relaxed">
        <p>
          Le <strong>sezioni libere</strong> sono un array discriminato per <code>type</code>: ogni
          sezione (problemi, moduli, architettura, ecc.) ha forma diversa. L&apos;editor avanzato
          qui permette di modificare direttamente il JSON.
        </p>
        <p className="mt-2 text-zinc-600">
          Tipi supportati: <code>problemi</code>, <code>risultati-analisi</code>,{' '}
          <code>soluzione</code>, <code>architettura</code>, <code>flusso</code>,{' '}
          <code>wireframe</code>, <code>tracciati</code>, <code>regole</code>, <code>moduli</code>,{' '}
          <code>deliverable</code>, <code>risorse</code>, <code>confronto</code>, <code>raw</code>.
        </p>
        {Object.keys(sectionCounts).length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {Object.entries(sectionCounts).map(([type, n]) => (
              <Badge key={type} variant="muted">
                {type} × {n}
              </Badge>
            ))}
          </div>
        )}
      </div>
      <Textarea
        value={jsonText}
        onChange={(e) => setJsonText(e.target.value)}
        rows={20}
        className="font-mono text-xs leading-relaxed"
        spellCheck={false}
      />
      {parseError && (
        <div className="text-sm text-pink-300 rounded-md bg-pink-500/10 border border-pink-500/30 px-3 py-2">
          {parseError}
        </div>
      )}
      <div className="flex gap-2">
        <Button variant="secondary" size="sm" onClick={applyJson}>
          Applica al content
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setJsonText(JSON.stringify(content.sections ?? [], null, 2))}
        >
          Ripristina
        </Button>
      </div>
    </div>
  )
}

// ============================================================
// Tab: Pricing + Configurations
// ============================================================
function PricingTab({
  content,
  update,
  setContent,
}: {
  content: ProposalContent
  update: (path: string[], v: unknown) => void
  setContent: (next: ProposalContent) => void
}) {
  const pricing = content.pricing ?? { voci: [], totaleProgettoEur: 0, vatRate: 22 }
  const voci = pricing.voci ?? []

  const total = voci.reduce(
    (s: number, v: { priceEur?: number }) => s + (Number(v.priceEur) || 0),
    0,
  )

  function recomputeTotal() {
    setContent({
      ...content,
      pricing: { ...pricing, totaleProgettoEur: total },
    })
  }

  return (
    <div className="space-y-5">
      <div>
        <div className="flex items-center justify-between mb-2">
          <Label>Voci di prezzo</Label>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => update(['pricing', 'voci'], [...voci, { riga: '', priceEur: 0 }])}
          >
            + Aggiungi voce
          </Button>
        </div>
        <div className="space-y-2">
          {voci.map(
            (
              v: { riga?: string; dettaglio?: string; priceEur?: number; hours?: number },
              i: number,
            ) => (
              <div
                // biome-ignore lint/suspicious/noArrayIndexKey: editable list
                key={i}
                className="grid grid-cols-12 gap-2 items-start"
              >
                <Input
                  className="col-span-5"
                  value={v.riga ?? ''}
                  placeholder="Voce"
                  onChange={(e) => {
                    const next = [...voci]
                    next[i] = { ...next[i], riga: e.target.value }
                    update(['pricing', 'voci'], next)
                  }}
                />
                <Input
                  className="col-span-3"
                  value={v.dettaglio ?? ''}
                  placeholder="Dettaglio (opz.)"
                  onChange={(e) => {
                    const next = [...voci]
                    next[i] = { ...next[i], dettaglio: e.target.value }
                    update(['pricing', 'voci'], next)
                  }}
                />
                <Input
                  className="col-span-1"
                  type="number"
                  value={v.hours ?? ''}
                  placeholder="Ore"
                  onChange={(e) => {
                    const next = [...voci]
                    next[i] = {
                      ...next[i],
                      hours: e.target.value ? Number(e.target.value) : undefined,
                    }
                    update(['pricing', 'voci'], next)
                  }}
                />
                <Input
                  className="col-span-2"
                  type="number"
                  step="0.01"
                  value={v.priceEur ?? 0}
                  placeholder="€"
                  onChange={(e) => {
                    const next = [...voci]
                    next[i] = { ...next[i], priceEur: Number(e.target.value) || 0 }
                    update(['pricing', 'voci'], next)
                  }}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    update(
                      ['pricing', 'voci'],
                      voci.filter((_: unknown, j: number) => j !== i),
                    )
                  }
                  className="col-span-1"
                >
                  ✕
                </Button>
              </div>
            ),
          )}
        </div>
        <div className="mt-3 flex items-center justify-between p-3 rounded-md bg-zinc-950/50 border border-zinc-800/60">
          <span className="text-xs uppercase tracking-wider text-zinc-500">Subtotale voci</span>
          <span className="font-mono text-zinc-200">
            € {total.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
          </span>
          <Button variant="ghost" size="sm" onClick={recomputeTotal}>
            Aggiorna totale progetto
          </Button>
        </div>
        <div className="mt-3 grid grid-cols-3 gap-3">
          <div>
            <Label>Totale progetto (€)</Label>
            <Input
              type="number"
              step="0.01"
              value={pricing.totaleProgettoEur ?? 0}
              onChange={(e) =>
                update(['pricing', 'totaleProgettoEur'], Number(e.target.value) || 0)
              }
            />
          </div>
          <div>
            <Label>Sconto bundle (€)</Label>
            <Input
              type="number"
              step="0.01"
              value={pricing.scontoBundleEur ?? ''}
              onChange={(e) =>
                update(
                  ['pricing', 'scontoBundleEur'],
                  e.target.value ? Number(e.target.value) : undefined,
                )
              }
            />
          </div>
          <div>
            <Label>VAT (%)</Label>
            <Input
              type="number"
              value={pricing.vatRate ?? 22}
              onChange={(e) => update(['pricing', 'vatRate'], Number(e.target.value) || 22)}
            />
          </div>
        </div>
      </div>

      <div className="border-t border-zinc-800 pt-4">
        <Label>Configurazioni MVP (opzionale)</Label>
        <p className="text-xs text-zinc-500 mb-2">
          Modificali via JSON editor delle sezioni; oppure lascia vuoto se progetto a prezzo unico.
        </p>
      </div>
    </div>
  )
}

// ============================================================
// Tab: Payment + Maintenance
// ============================================================
function PaymentTab({
  content,
  update,
  setContent,
}: {
  content: ProposalContent
  update: (path: string[], v: unknown) => void
  setContent: (next: ProposalContent) => void
}) {
  const payment = content.payment ?? { modello: 'percent', perConfigurazione: [], scadenza: '' }
  const configs = payment.perConfigurazione ?? []
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Modello</Label>
          <Select
            value={payment.modello ?? 'percent'}
            onChange={(e) => update(['payment', 'modello'], e.currentTarget.value)}
          >
            <option value="percent">Percentuale</option>
            <option value="fixed-tranche">Tranche fisse</option>
          </Select>
        </div>
        <div>
          <Label>Scadenza pagamento</Label>
          <Input
            value={payment.scadenza ?? ''}
            onChange={(e) => update(['payment', 'scadenza'], e.target.value)}
            placeholder="30 giorni data fattura"
          />
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <Label>Piani per configurazione</Label>
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              update(
                ['payment', 'perConfigurazione'],
                [
                  ...configs,
                  {
                    configKey: 'unica',
                    tranches: [{ label: 'Anticipo', percentuale: 30 }],
                  },
                ],
              )
            }
          >
            + Aggiungi piano
          </Button>
        </div>
        {configs.map(
          (
            cfg: {
              configKey: string
              tranches: Array<{
                label: string
                percentuale?: number
                importoEur?: number
                condizione?: string
              }>
            },
            ci: number,
          ) => (
            <div
              // biome-ignore lint/suspicious/noArrayIndexKey: editable list
              key={ci}
              className="rounded-lg border border-zinc-800 bg-zinc-950/40 p-3 mb-2"
            >
              <div className="flex items-center gap-2 mb-2">
                <Input
                  className="flex-1"
                  value={cfg.configKey}
                  placeholder="Config key (es. mvp-completo)"
                  onChange={(e) => {
                    const next = [...configs]
                    next[ci] = { ...next[ci], configKey: e.target.value }
                    update(['payment', 'perConfigurazione'], next)
                  }}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    update(
                      ['payment', 'perConfigurazione'],
                      configs.filter((_x: unknown, j: number) => j !== ci),
                    )
                  }
                >
                  ✕
                </Button>
              </div>
              <div className="space-y-2">
                {(cfg.tranches ?? []).map((tr, ti) => (
                  <div
                    // biome-ignore lint/suspicious/noArrayIndexKey: editable list
                    key={ti}
                    className="grid grid-cols-12 gap-2"
                  >
                    <Input
                      className="col-span-4"
                      value={tr.label}
                      placeholder="Label tranche"
                      onChange={(e) => {
                        const next = [...configs]
                        const tranches = [...(next[ci].tranches ?? [])]
                        tranches[ti] = { ...tranches[ti], label: e.target.value }
                        next[ci] = { ...next[ci], tranches }
                        update(['payment', 'perConfigurazione'], next)
                      }}
                    />
                    <Input
                      className="col-span-2"
                      type="number"
                      value={tr.percentuale ?? ''}
                      placeholder="%"
                      onChange={(e) => {
                        const next = [...configs]
                        const tranches = [...(next[ci].tranches ?? [])]
                        tranches[ti] = {
                          ...tranches[ti],
                          percentuale: e.target.value ? Number(e.target.value) : undefined,
                        }
                        next[ci] = { ...next[ci], tranches }
                        update(['payment', 'perConfigurazione'], next)
                      }}
                    />
                    <Input
                      className="col-span-2"
                      type="number"
                      step="0.01"
                      value={tr.importoEur ?? ''}
                      placeholder="€"
                      onChange={(e) => {
                        const next = [...configs]
                        const tranches = [...(next[ci].tranches ?? [])]
                        tranches[ti] = {
                          ...tranches[ti],
                          importoEur: e.target.value ? Number(e.target.value) : undefined,
                        }
                        next[ci] = { ...next[ci], tranches }
                        update(['payment', 'perConfigurazione'], next)
                      }}
                    />
                    <Input
                      className="col-span-3"
                      value={tr.condizione ?? ''}
                      placeholder="Condizione"
                      onChange={(e) => {
                        const next = [...configs]
                        const tranches = [...(next[ci].tranches ?? [])]
                        tranches[ti] = { ...tranches[ti], condizione: e.target.value || undefined }
                        next[ci] = { ...next[ci], tranches }
                        update(['payment', 'perConfigurazione'], next)
                      }}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const next = [...configs]
                        const cfgRow = next[ci]
                        if (!cfgRow) return
                        next[ci] = {
                          ...cfgRow,
                          tranches: (cfgRow.tranches ?? []).filter(
                            (_x: unknown, j: number) => j !== ti,
                          ),
                        }
                        update(['payment', 'perConfigurazione'], next)
                      }}
                      className="col-span-1"
                    >
                      ✕
                    </Button>
                  </div>
                ))}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    const next = [...configs]
                    next[ci] = {
                      ...next[ci],
                      tranches: [...(next[ci].tranches ?? []), { label: '', percentuale: 0 }],
                    }
                    update(['payment', 'perConfigurazione'], next)
                  }}
                >
                  + Aggiungi tranche
                </Button>
              </div>
            </div>
          ),
        )}
      </div>

      <div className="border-t border-zinc-800 pt-4">
        <Label>Coordinate bancarie (override)</Label>
        <p className="text-xs text-zinc-500 mb-2">
          Lascia vuoto per usare quelle del tenant_config (IBAN G soLutions).
        </p>
        <div className="grid grid-cols-2 gap-3">
          <Input
            placeholder="Intestatario"
            value={payment.coordinate?.intestatario ?? ''}
            onChange={(e) => update(['payment', 'coordinate', 'intestatario'], e.target.value)}
          />
          <Input
            placeholder="IBAN"
            value={payment.coordinate?.iban ?? ''}
            onChange={(e) => update(['payment', 'coordinate', 'iban'], e.target.value)}
          />
        </div>
      </div>

      <div className="border-t border-zinc-800 pt-4">
        <Label>Manutenzione (avanzato — usa JSON sezioni)</Label>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            if (content.maintenance) return
            setContent({
              ...content,
              maintenance: {
                garanziaInclusaMesi: 12,
                garanziaInclude: ['Correzione bug post-rilascio'],
                garanziaEsclude: ['Sviluppo nuove funzionalità'],
              },
            })
          }}
        >
          + Inizializza blocco manutenzione
        </Button>
        {content.maintenance && (
          <div className="mt-2 grid grid-cols-2 gap-3">
            <div>
              <Label>Garanzia (mesi)</Label>
              <Input
                type="number"
                value={content.maintenance.garanziaInclusaMesi ?? 12}
                onChange={(e) =>
                  update(['maintenance', 'garanziaInclusaMesi'], Number(e.target.value) || 12)
                }
              />
            </div>
            <div>
              <Label>Canone annuo (€)</Label>
              <Input
                type="number"
                step="0.01"
                value={content.maintenance.canoneAnnuo?.importoEur ?? ''}
                onChange={(e) =>
                  update(
                    ['maintenance', 'canoneAnnuo', 'importoEur'],
                    e.target.value ? Number(e.target.value) : undefined,
                  )
                }
              />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ============================================================
// Tab: Conditions
// ============================================================
function ConditionsTab({
  content,
  update,
}: {
  content: ProposalContent
  update: (path: string[], v: unknown) => void
}) {
  const c = content.conditions ?? { validitaGiorni: 30 }
  return (
    <div className="space-y-4">
      <div>
        <Label>Validità (giorni)</Label>
        <Input
          type="number"
          value={c.validitaGiorni ?? 30}
          onChange={(e) => update(['conditions', 'validitaGiorni'], Number(e.target.value) || 30)}
        />
      </div>
      <div>
        <Label>Proprietà intellettuale</Label>
        <Textarea
          value={c.proprietaIntellettuale ?? ''}
          onChange={(e) => update(['conditions', 'proprietaIntellettuale'], e.target.value)}
          rows={3}
        />
      </div>
      <div>
        <Label>Riservatezza</Label>
        <Textarea
          value={c.riservatezza ?? ''}
          onChange={(e) => update(['conditions', 'riservatezza'], e.target.value)}
          rows={3}
        />
      </div>
      <div>
        <Label>Trasferte / costi esclusi</Label>
        <Textarea
          value={c.trasferte ?? ''}
          onChange={(e) => update(['conditions', 'trasferte'], e.target.value)}
          rows={2}
        />
      </div>
    </div>
  )
}

// ============================================================
// Tab: Acceptance
// ============================================================
function AcceptanceTab({
  content,
  update,
}: {
  content: ProposalContent
  update: (path: string[], v: unknown) => void
}) {
  const a = content.acceptance ?? { firmaCliente: {}, firmaFornitore: {} }
  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-medium text-zinc-100 mb-2 text-sm">Firma cliente</h3>
        <div className="grid grid-cols-2 gap-3">
          <Input
            placeholder="Ragione sociale cliente"
            value={a.firmaCliente?.ragioneSociale ?? ''}
            onChange={(e) =>
              update(['acceptance', 'firmaCliente', 'ragioneSociale'], e.target.value)
            }
          />
          <Input
            placeholder="Referente firmatario"
            value={a.firmaCliente?.referente ?? ''}
            onChange={(e) => update(['acceptance', 'firmaCliente', 'referente'], e.target.value)}
          />
        </div>
      </div>
      <div>
        <h3 className="font-medium text-zinc-100 mb-2 text-sm">Firma fornitore (G soLutions)</h3>
        <div className="grid grid-cols-2 gap-3">
          <Input
            placeholder="Ragione sociale fornitore"
            value={a.firmaFornitore?.ragioneSociale ?? ''}
            onChange={(e) =>
              update(['acceptance', 'firmaFornitore', 'ragioneSociale'], e.target.value)
            }
          />
          <Input
            placeholder="Luogo, data (es. Formigine, 12/05/2026)"
            value={a.firmaFornitore?.luogoData ?? ''}
            onChange={(e) => update(['acceptance', 'firmaFornitore', 'luogoData'], e.target.value)}
          />
        </div>
      </div>
      <p className="text-xs text-zinc-500 italic">
        Firma e timbro G soLutions vengono caricati dal renderer dall'asset MinIO indicato in{' '}
        <code>tenant_config.branding.firmaAssetKey</code> e <code>timbroAssetKey</code> (file
        caricati via Tenant → Branding o <code>POST /api/storage/branding/:filename</code>).
      </p>
    </div>
  )
}

// ============================================================
// helpers
// ============================================================
function setDeep(obj: ProposalContent, path: string[], value: unknown): ProposalContent {
  if (path.length === 0) return value as ProposalContent
  const head = path[0]
  const rest = path.slice(1)
  if (head === undefined) return value as ProposalContent
  const current = (obj?.[head] as ProposalContent) ?? {}
  return {
    ...(obj ?? {}),
    [head]: rest.length === 0 ? value : setDeep(current, rest, value),
  }
}
